import {
  CombinedCampaignStatus,
  getCombinedCampaignStatuses,
  useCombinedCampaignStatuses,
} from "@/api/app-configuration.api";
import {
  getCampaignsQueryOptions,
  listCampaignsParamsSchema,
} from "@/api/campaign.api";
import { createUsersQueryOptions } from "@/api/users.api";
import {
  CampaignLeadsGraph,
  CampaignLeadsGraphProps,
} from "@/components/campaign/CampaignLeadsGraph";
import {
  AdjustScheduledCampaignWeek,
  schedulesSchema,
} from "@/components/campaign/configuration/AdjustScheduledCampaignWeek";
import { NewCampaignButton } from "@/components/campaign/NewCampaignButton";
import { Search } from "@/components/generic/Search";
import {
  SelectFilter,
  useSelectFilter,
} from "@/components/generic/SelectFilter";
import { createTypedTable } from "@/components/generic/Table";
import { MainWindow, PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import { StickyElement } from "@/components/layout/StickyElement";
import { TrialCTAModal } from "@/components/trial/TrialCTAModal";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import { trialText } from "@/hooks/use-trial-period";
import { Route as BuyCreditsRoute } from "@/routes/_dashboard/buy-credits";
import { isAdmin as isUserAdmin } from "@/services/auth.service";
import {
  Campaign,
  CampaignState,
  campaignStates,
  CampaignStatus,
  campaignStatuses,
  campaignTypes,
  planningTypes,
} from "@/types/campaign";
import { getEstimate } from "@/utils/campaign-helpers";
import { formatDate2, UTCWeekPeriod } from "@/utils/dates";
import { formatNumber } from "@/utils/number-utils";
import { UTCDate } from "@date-fns/utc";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  linkOptions,
  NavigateOptions,
  redirect,
} from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortDescriptor } from "react-stately";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import z from "zod";

const searchSchema = listCampaignsParamsSchema.pick({
  name: true,
  type: true,
  status: true,
  state: true,
  creatorId: true,
  responsibleId: true,
  orderBy: true,
  desc: true,
});

const historyStateSchema = z
  .object({
    adjustScheduledCampaignWeek: z
      .object({
        campaignId: z.number(),
        week: z.object({
          start: z.date().transform((date) => new UTCDate(date)),
          end: z.date().transform((date) => new UTCDate(date)),
        }),
        values: schedulesSchema,
        skipCreditCheck: z.boolean(),
        autoSubmit: z.boolean(),
      })
      .optional(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute("/_dashboard/campaigns/")({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Kampagner",
      isTitleRoot: true,
    },
  },
  validateSearch: searchSchema.parse,
  beforeLoad: async ({ context, search, location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    const usersQueryOptions = createUsersQueryOptions({ nameFilter: "" });

    const usersQueryData = await context.queryClient.ensureInfiniteQueryData({
      ...usersQueryOptions,
      revalidateIfStale: false,
    });

    const { combinedCampaignStatuses } = getCombinedCampaignStatuses(
      context.appConfiguration,
    );

    const statusFilterValues = combinedCampaignStatuses
      .filter((cS) => !(cS.status === "Active" && cS.state !== "Normal"))
      .map((combinedStatus) => {
        let value = combinedStatus;
        if (combinedStatus.status === "Active") {
          value = {
            ...value,
            state: null,
          };
        }
        return value;
      });

    // Sanitize and update search
    {
      const newSearch: typeof search = {};

      function sanitizeSearchParam<K extends keyof typeof search>(
        key: K,
        sanitizer: (value: (typeof search)[K]) => (typeof search)[K],
      ) {
        const value = search[key];
        const newValue = sanitizer(value);
        if (JSON.stringify(value) !== JSON.stringify(newValue)) {
          newSearch[key] = newValue;
        }
      }

      function sanitizeUniqueArray<T>(
        values: T[] | undefined,
        validValues: Set<T>,
      ) {
        const newValues = [
          ...new Set(values?.filter((value) => validValues.has(value))),
        ];
        return newValues.length ? newValues : undefined;
      }

      const userIds = new Set(
        usersQueryData.pages.flatMap((page) =>
          page.results.map((user) => user.id),
        ),
      );

      if (search.status === undefined && search.state === undefined) {
        const statuses = new Set<string>();
        const states = new Set<string>();
        for (const { status, state } of statusFilterValues) {
          if (status === "Archived") continue;
          statuses.add(status);
          if (state) states.add(state);
        }
        newSearch.status = [...statuses];
        newSearch.state = [...states];
      } else {
        sanitizeSearchParam(
          "status",
          (value) =>
            sanitizeUniqueArray(
              value,
              new Set(
                campaignStatuses.filter(
                  (s) => isUserAdmin(context.user) || !["Inactive"].includes(s),
                ),
              ),
            ) ?? [],
        );
        sanitizeSearchParam(
          "state",
          (value) => sanitizeUniqueArray(value, new Set(campaignStates)) ?? [],
        );
      }

      sanitizeSearchParam("type", (value) =>
        sanitizeUniqueArray(value, new Set(campaignTypes)),
      );
      sanitizeSearchParam("creatorId", (value) =>
        sanitizeUniqueArray(value, userIds),
      );
      sanitizeSearchParam("responsibleId", (value) =>
        sanitizeUniqueArray(value, userIds),
      );
      sanitizeSearchParam("orderBy", (value) =>
        new Set<unknown>(["created"]).has(value) ? value : "created",
      );
      sanitizeSearchParam("desc", (value) => value ?? true);

      if (Object.keys(newSearch).length) {
        throw redirect({
          replace: true,
          from: Route.fullPath,
          search: {
            ...search,
            ...newSearch,
          },
          state: true,
        });
      }
    }

    const useUsersQuery = () =>
      useInfiniteQuery({
        ...usersQueryOptions,
        initialData: usersQueryData,
      });

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
      useUsersQuery,
      statusFilterValues,
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();

  const isAdmin = useMemo(() => isUserAdmin(context.user), [context.user]);

  return (
    <MainWindow className="flex flex-col">
      <PageHeader
        title="Kampagner"
        actions={isAdmin && <NewCampaignButton />}
      />

      <PageContent className="flex grow flex-col">
        <div className="flex grow flex-col">
          <CampaignsTable />
        </div>
      </PageContent>
    </MainWindow>
  );
}

const campaignTypesMap = new Map(
  planningTypes.map((type) => [String(type.type), type]),
);

type CampaignsTableItem = {
  id: number;
  campaign: Campaign;
  name: string;
  period: string;
  leadsPerWeek: string;
  deliveredLeads: string;
  remainingLeads: string;
  responsible: string;
  owner: string;
  status: CombinedCampaignStatus | null;
};

const typedTable = createTypedTable<CampaignsTableItem>();

const { TableHeader: CampaignsTableHeader, TableBody: CampaignsTableBody } =
  typedTable;

const campaignsTableColumns = typedTable.createColumns([
  {
    id: "selected",
    name: "Valgt",
    isCheckbox: true,
    width: 20,
    grow: 0,
  },
  {
    id: "type",
    name: "Type",
    width: 24,
    grow: 0,
    renderCell: ({ campaign: { type } }) => {
      const Icon = campaignTypesMap.get(type)?.icon;

      return (
        <div className="flex w-full items-center justify-center">
          {Icon && <Icon size={16} />}
        </div>
      );
    },
  },
  {
    id: "name",
    name: "Navn",
    isRowHeader: true,
    width: 96,
  },
  {
    id: "responsible",
    name: "Ansvarlig",
    width: 80,
  },
  {
    id: "status",
    name: "Status",
    width: 80,
    grow: 0,
    renderCell: ({ status }) =>
      status && (
        <span className={status.color ?? undefined}>{status.name}</span>
      ),
  },
  {
    id: "period",
    name: "Periode",
    width: 148,
    grow: 0,
  },
  {
    id: "leadsPerWeek",
    name: "Leads pr. uge",
    width: 72,
    className: "text-right",
  },
  {
    id: "deliveredLeads",
    name: "Leads leveret",
    width: 72,
    className: "text-right",
  },
  {
    id: "remainingLeads",
    name: "Fremtidige leads",
    width: 88,
    className: "text-right",
  },
]);

function getPeriod(campaign: Campaign) {
  const startDate = campaign.chosenStartDate
    ? formatDate2(campaign.chosenStartDate)
    : null;
  const endDate = campaign.chosenEndDate
    ? formatDate2(campaign.chosenEndDate)
    : null;
  if (!startDate) return "";
  if (!endDate) return `${startDate} - ingen slutdato`;
  return `${startDate} - ${endDate}`;
}

export function CampaignsTable() {
  const context = Route.useRouteContext();
  const { statusFilterValues } = context;

  const historyState = context.useHistoryState();

  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  useHorizontalScrollSync(headerScrollRef, bodyScrollRef);

  const { getCombinedCampaignStatus } = useCombinedCampaignStatuses();

  const isAdmin = useMemo(() => isUserAdmin(context.user), [context.user]);

  const { data: campaigns, isFetching } = useQuery({
    ...getCampaignsQueryOptions({
      ...search,
      includeStatistics: true,
    }),
    placeholderData: keepPreviousData,
  });

  const campaignsById = useMemo(
    () => new Map(campaigns?.map((campaign) => [campaign.id, campaign])),
    [campaigns],
  );

  const usersQuery = context.useUsersQuery();
  const users = useMemo(
    () => usersQuery.data.pages.flatMap((page) => page.results),
    [usersQuery.data.pages],
  );
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );

  const columns = useMemo(
    () =>
      isAdmin
        ? campaignsTableColumns
        : campaignsTableColumns.filter((column) => column.id !== "responsible"),
    [isAdmin],
  );

  const items = useMemo(
    () =>
      (campaigns ?? [])
        .map<CampaignsTableItem | null>((campaign) => {
          if (campaign.id === undefined) return null;

          return {
            id: campaign.id,
            campaign,
            name: campaign.name,
            period: getPeriod(campaign),
            leadsPerWeek:
              campaign.type === "Scheduled"
                ? formatNumber(
                    campaign.schedules.reduce(
                      (total, schedule) => total + schedule.amount,
                      0,
                    ),
                  )
                : "",
            deliveredLeads: formatNumber(campaign.statistics?.total ?? 0),
            remainingLeads:
              campaign.estimatedRemaining !== null
                ? getEstimate(campaign.estimatedRemaining)
                : "",
            responsible:
              (campaign.responsibleId &&
                usersById.get(campaign.responsibleId)?.name) ||
              "",
            owner:
              (campaign.creatorId && usersById.get(campaign.creatorId)?.name) ||
              "",
            status: getCombinedCampaignStatus(campaign.status, campaign.state),
          };
        })
        .filter((item) => item !== null),
    [campaigns, usersById, getCombinedCampaignStatus],
  );

  const [searchString, setSearchString] = useState("");

  const typeFilter = useSelectFilter({
    items: useMemo(
      () => [
        { key: "OnDemand", label: "On-demand kampagner", value: "OnDemand" },
        { key: "Scheduled", label: "Flowkampagner", value: "Scheduled" },
        { key: "Triggered", label: "Triggerkampagner", value: "Triggered" },
      ],
      [],
    ),
  });

  const responsibleFilter = useSelectFilter({
    items: useMemo(
      () =>
        users.map((user) => ({
          key: user.id,
          label: user.name,
          value: user.id,
        })),
      [users],
    ),
  });

  const creatorFilter = useSelectFilter({
    items: useMemo(
      () =>
        users.map((user) => ({
          key: user.id,
          label: user.name,
          value: user.id,
        })),
      [users],
    ),
  });

  const statusFilter = useSelectFilter({
    items: useMemo(
      () =>
        statusFilterValues
          .filter(
            (s) =>
              isUserAdmin(context.user) || !["Inactive"].includes(s.status),
          )
          .map((combinedStatus) => ({
            key: combinedStatus.name,
            label: combinedStatus.name,
            value: combinedStatus,
          })),
      [context.user, statusFilterValues],
    ),
  });

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "created",
    direction: "descending",
  });

  const [selectedIds, setSelectedIds] = useState(new Set<number>());

  const debouncedSelectedIds = useDebounce(selectedIds, 800);

  const selectedCampaigns = useMemo(
    () =>
      debouncedSelectedIds.size
        ? campaigns?.filter((campaign) => debouncedSelectedIds.has(campaign.id))
        : campaigns,
    [debouncedSelectedIds, campaigns],
  );

  let skipSearchUpdate = false;

  useEffect(() => {
    if (search.name) {
      setSearchString(search.name);
    }

    if (search.type) {
      typeFilter.setSelectedKeys(new Set(search.type));
    }

    if (search.responsibleId) {
      responsibleFilter.setSelectedKeys(new Set(search.responsibleId));
    }

    if (search.creatorId) {
      creatorFilter.setSelectedKeys(new Set(search.creatorId));
    }

    if (search.status || search.state) {
      const selectedStatuses = new Set(search.status);
      const selectedStates = new Set(search.state);
      const matches = new Set<string>();
      for (const { value: combinedStatus } of statusFilter.items) {
        if (
          selectedStatuses.has(combinedStatus.status) &&
          (!combinedStatus.state || selectedStates.has(combinedStatus.state))
        ) {
          matches.add(combinedStatus.name);
        }
      }
      statusFilter.setSelectedKeys(matches);
    }

    if (search.orderBy || search.desc !== undefined) {
      setSortDescriptor({
        column: search.orderBy ?? "created",
        direction: (search.desc ?? true) ? "descending" : "ascending",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    skipSearchUpdate = true;
  }, []);

  const newSearch = useMemo(() => {
    const statuses = new Set<CampaignStatus>();
    const states = new Set<CampaignState>();
    for (const { status, state } of statusFilter.selectedValues) {
      statuses.add(status);
      if (state) states.add(state);
    }

    return {
      name: searchString || undefined,
      type: typeFilter.selectedValues.length
        ? typeFilter.selectedValues
        : undefined,
      responsibleId: responsibleFilter.selectedValues.length
        ? responsibleFilter.selectedValues
        : undefined,
      creatorId: creatorFilter.selectedValues.length
        ? creatorFilter.selectedValues
        : undefined,
      status: [...statuses],
      state: [...states],
      orderBy: sortDescriptor.column.toString(),
      desc: sortDescriptor.direction === "descending",
    } satisfies typeof search;
  }, [
    searchString,
    typeFilter.selectedValues,
    responsibleFilter.selectedValues,
    creatorFilter.selectedValues,
    statusFilter.selectedValues,
    sortDescriptor.column,
    sortDescriptor.direction,
  ]);

  const debouncedNewSearch = useDebounce(newSearch, 300);

  useEffect(() => {
    if (skipSearchUpdate) return;

    let diffFound = false;

    for (const key of Object.keys(newSearch) as (keyof typeof newSearch)[]) {
      const newValue = newSearch[key];
      const oldValue = search[key];
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        diffFound = true;
        break;
      }
    }

    if (diffFound) {
      setSelectedIds(new Set());
      navigate({
        from: Route.fullPath,
        search: {
          ...search,
          ...newSearch,
        },
        state: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipSearchUpdate, debouncedNewSearch]);

  const buildItemLink = useCallback(
    (item: CampaignsTableItem): NavigateOptions => ({
      to: "/campaigns/$id",
      params: { id: item.id },
    }),
    [],
  );

  type View =
    | { name: "campaigns-table" }
    | {
        name: "adjust-campaign-week-schedule";
        key: string;
        campaignId: number;
        week: UTCWeekPeriod;
        values?: z.infer<typeof schedulesSchema>;
        skipCreditCheck?: boolean;
        autoSubmit?: boolean;
      };

  const createAdjustCampaignWeekScheduleView = ({
    campaignId,
    week,
    values,
    skipCreditCheck,
    autoSubmit,
  }: Omit<
    View & { name: "adjust-campaign-week-schedule" },
    "name" | "key"
  >) => ({
    name: "adjust-campaign-week-schedule" as const,
    key: `${campaignId},${week.start.getTime()}`,
    campaignId,
    week,
    values,
    skipCreditCheck,
    autoSubmit,
  });

  const [view, setView] = useState<View>(() =>
    historyState?.adjustScheduledCampaignWeek
      ? createAdjustCampaignWeekScheduleView(
          historyState.adjustScheduledCampaignWeek,
        )
      : { name: "campaigns-table" },
  );

  const [trialCTAModalProps, setTrialCTAModalProps] = useState<{
    title: string;
  }>();

  const onAdjustWeekSchedule = useMemo<
    CampaignLeadsGraphProps["onAdjustWeekSchedule"]
  >(() => {
    if (!isAdmin) return undefined;

    if (context.trialPeriod)
      return () => {
        setTrialCTAModalProps({
          title: trialText,
        });
      };

    return ({ campaignId, week }) => {
      setView(createAdjustCampaignWeekScheduleView({ campaignId, week }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.trialPeriod, isAdmin]);

  const [selectedWeek, setSelectedWeek] = useState<UTCWeekPeriod | null>(null);

  return (
    <div className="flex h-full flex-col">
      <CampaignLeadsGraph
        campaigns={selectedCampaigns}
        isLoadingCampaigns={
          isFetching ||
          newSearch !== debouncedNewSearch ||
          selectedIds !== debouncedSelectedIds
        }
        title=""
        noDataText="Intet data for de valgte kampagner"
        selectedWeek={
          selectedWeek ??
          (view.name === "adjust-campaign-week-schedule" ? view.week : null)
        }
        setSelectedWeek={setSelectedWeek}
        onAdjustWeekSchedule={onAdjustWeekSchedule}
      />

      <div className="relative grid h-full *:col-start-1 *:row-start-1 *:min-w-0">
        <AnimatePresence>
          {view.name === "campaigns-table" ? (
            <motion.div
              key={view.name}
              className="relative flex flex-col"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.3 },
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <typedTable.Table
                aria-label="Kampagner"
                className="max-w-full grow"
                columns={columns}
                items={items}
                sortDescriptor={sortDescriptor}
                setSortDescriptor={setSortDescriptor}
                buildItemLink={buildItemLink}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
              >
                <StickyElement
                  stickyProps={{
                    className: "bg-theme-background",
                  }}
                >
                  <div
                    id="scroll-headers"
                    className="overflow-x-scroll px-8 xl:px-14"
                  >
                    <div className="my-4 flex items-center gap-x-6">
                      <Search
                        className="-mx-2 grow"
                        value={searchString}
                        onChange={(e) => setSearchString(e.currentTarget.value)}
                      />

                      <div className="flex items-center gap-x-6">
                        <SelectFilter
                          buttonLabel="Type"
                          selectionMode="multiple"
                          items={typeFilter.items}
                          selectedKeys={typeFilter.selectedKeys}
                          onSelectionChange={typeFilter.setSelectedKeys}
                        />

                        {isAdmin && (
                          <>
                            <SelectFilter
                              buttonLabel="Ansvarlig"
                              selectionMode="multiple"
                              items={responsibleFilter.items}
                              selectedKeys={responsibleFilter.selectedKeys}
                              onSelectionChange={
                                responsibleFilter.setSelectedKeys
                              }
                            />

                            <SelectFilter
                              buttonLabel="Oprettet af"
                              selectionMode="multiple"
                              items={creatorFilter.items}
                              selectedKeys={creatorFilter.selectedKeys}
                              onSelectionChange={creatorFilter.setSelectedKeys}
                            />
                          </>
                        )}

                        <SelectFilter
                          buttonLabel="Status"
                          selectionMode="multiple"
                          items={statusFilter.items}
                          selectedKeys={statusFilter.selectedKeys}
                          onSelectionChange={statusFilter.setSelectedKeys}
                        />
                      </div>
                    </div>

                    <div className="border-separator mb-3" />
                  </div>

                  <CampaignsTableHeader
                    id="scroll-headers"
                    ref={headerScrollRef}
                    className="overflow-x-scroll"
                  />
                </StickyElement>

                <CampaignsTableBody
                  id="scroll-headers"
                  ref={bodyScrollRef}
                  className="min-w-0 overflow-x-scroll"
                />

                <div className="grid h-14 shrink-0 grow px-14 py-5 *:col-start-1 *:row-start-1">
                  <AnimatePresence>
                    {(() => {
                      let key = "";
                      let text = "";

                      if (isFetching) {
                        key = "loading";
                        text = "Henter kampagner...";
                      } else if (!campaigns?.length) {
                        key = "no-results";
                        text = "Ingen kampagner fundet";
                      }

                      if (!key) return null;

                      return (
                        <motion.div
                          key={key}
                          className="flex items-center justify-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <span
                            className={twMerge(
                              "text-theme-on-background-variant text-center text-xs font-medium",
                              key === "loading" && "animate-pulse",
                            )}
                          >
                            {text}
                          </span>
                        </motion.div>
                      );
                    })()}
                  </AnimatePresence>
                </div>
              </typedTable.Table>
            </motion.div>
          ) : view.name === "adjust-campaign-week-schedule" ? (
            <motion.div
              key={view.name}
              className="relative z-1 flex flex-col p-14 pt-7"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { duration: 0.3 },
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SettingsContainer
                key={view.key}
                className="h-full"
                animate={false}
                onClickBack={() => {
                  setView({ name: "campaigns-table" });
                }}
                responsivePadding={false}
              >
                {(() => {
                  const campaign = campaignsById.get(view.campaignId);
                  return campaign ? (
                    <AdjustScheduledCampaignWeek
                      campaign={campaign}
                      week={view.week}
                      values={view.values}
                      autoSubmit={view.autoSubmit}
                      skipCreditCheck={view.skipCreditCheck}
                      goToBuyCredits={async ({ neededCredits, schedules }) => {
                        const adjustScheduledCampaignWeekState = {
                          campaignId: view.campaignId,
                          week: view.week,
                          values: schedules,
                          skipCreditCheck: false,
                          autoSubmit: false,
                        };

                        await navigate({
                          replace: true,
                          state: {
                            routeStates: {
                              [Route.id]: {
                                adjustScheduledCampaignWeek:
                                  adjustScheduledCampaignWeekState,
                              },
                            },
                          },
                        });

                        const createNavigateOptions = (
                          shouldContinue: boolean,
                        ) =>
                          linkOptions({
                            to: Route.path,
                            search,
                            state: {
                              routeStates: {
                                [Route.id]: {
                                  adjustScheduledCampaignWeek: {
                                    ...adjustScheduledCampaignWeekState,
                                    skipCreditCheck: shouldContinue,
                                    autoSubmit: shouldContinue,
                                  },
                                },
                              },
                            },
                          });

                        await navigate({
                          to: "/buy-credits",
                          state: {
                            routeStates: {
                              [BuyCreditsRoute.id]: {
                                neededCredits,
                                closeNavigateOptions:
                                  createNavigateOptions(false),
                                successNavigateOptions:
                                  createNavigateOptions(true),
                                skipNavigateOptions:
                                  createNavigateOptions(true),
                              },
                            },
                          },
                        });
                      }}
                      onSuccess={async () => {
                        toast("Kampagne opdateret.");
                        setView({ name: "campaigns-table" });
                        await navigate({
                          replace: true,
                          state: (prev) => ({
                            ...prev,
                            routeStates: {
                              ...prev.routeStates,
                              [Route.id]: {
                                ...prev.routeStates?.[Route.id],
                                adjustScheduledCampaignWeek: undefined,
                              },
                            },
                          }),
                        });
                      }}
                    />
                  ) : null;
                })()}
              </SettingsContainer>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <TrialCTAModal
        isOpen={!!trialCTAModalProps}
        onOpenChange={(open) =>
          setTrialCTAModalProps(open ? trialCTAModalProps : undefined)
        }
        title=""
        {...trialCTAModalProps}
      />
    </div>
  );
}
